import React from "react";
import { HeartPulse, ArrowRight } from "lucide-react";

const HREF = "https://bosshealth.in";
const IMG = (name) => `${process.env.PUBLIC_URL}/ads/${name}`;

const BENEFITS = [
  "Personalized health card",
  "Doctor directory",
  "Discounted check-ups",
  "Medicine reminders",
  "Ambulance assistance",
  "Health & travel insurance",
];

// Confetti positions (gold bokeh) echoing the reference promo banner.
const CONFETTI = [
  { top: "14%", left: "8%", s: 5, o: 0.7 },
  { top: "72%", left: "14%", s: 4, o: 0.5 },
  { top: "24%", left: "40%", s: 3, o: 0.6 },
  { top: "80%", left: "55%", s: 5, o: 0.5 },
  { top: "18%", left: "76%", s: 4, o: 0.6 },
  { top: "64%", left: "88%", s: 6, o: 0.7 },
];

// Horizontal affiliate promo banner for bosshealth.in (Healthboss).
// Premium dark + gold aesthetic inspired by a full-width app promo strip.
export function HealthbossAd({ className = "" }) {
  return (
    <a
      href={HREF}
      target="_blank"
      rel="sponsored noopener noreferrer"
      data-testid="healthboss-ad"
      aria-label="Sponsored: Register on Healthboss (bosshealth.in)"
      className={`group relative block w-full overflow-hidden rounded-3xl border border-[#c9a24b]/30 shadow-[0_18px_50px_-18px_rgba(11,42,61,0.6)] transition-transform duration-300 hover:-translate-y-0.5 ${className}`}
      style={{ background: "radial-gradient(120% 140% at 50% 0%, #114a5e 0%, #0c3244 45%, #071f2b 100%)" }}
    >
      {/* Gold rays + glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{ background: "repeating-conic-gradient(from 0deg at 50% 42%, rgba(246,197,0,0.9) 0deg 1deg, transparent 1deg 9deg)" }}
        aria-hidden
      />
      <div className="pointer-events-none absolute left-1/2 top-0 h-56 w-[36rem] -translate-x-1/2 rounded-full bg-[#f6c500]/20 blur-3xl" aria-hidden />
      {CONFETTI.map((c, i) => (
        <span key={i} aria-hidden className="pointer-events-none absolute rounded-full bg-[#f6c500]"
          style={{ top: c.top, left: c.left, width: c.s, height: c.s, opacity: c.o }} />
      ))}

      {/* AD label */}
      <span className="absolute right-3 top-3 z-10 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#e8d7a8]">
        Ad
      </span>

      <div className="relative flex flex-col items-center gap-5 px-6 py-7 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-10 sm:text-left">
        {/* Left: brand + headline + benefits */}
        <div className="min-w-0 sm:flex-1">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-2.5 py-1">
            <img src={IMG("logo.jpg")} alt="Healthboss" className="h-4 w-auto" />
          </div>
          <h3 className="font-heading text-2xl font-black leading-tight text-white sm:text-3xl">
            Start your <span className="text-[#f6c500]">wellness journey</span>
          </h3>
          <p className="mt-1 text-sm font-semibold text-[#bfe0e6]">
            One app, multiple benefits — <span className="text-[#f6c500] font-black">₹99</span> lifetime full access
          </p>
          <ul className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 sm:justify-start">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-center gap-1 text-[11px] font-semibold text-[#dcecef]">
                <HeartPulse size={12} className="text-[#6cbf3f]" /> {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: CTA */}
        <div className="flex shrink-0 flex-col items-center gap-2">
          <span
            data-testid="healthboss-ad-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#f6c500] px-7 py-3 text-[15px] font-black text-[#0b2a3d] shadow-[0_8px_24px_-6px_rgba(246,197,0,0.6)] transition-colors group-hover:bg-[#ffd648]"
          >
            Register to join now
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
          </span>
          <p className="text-[10px] font-bold text-[#9fc0c8]">Tap to join us · bosshealth.in</p>
        </div>
      </div>

      {/* Carousel-style dots (decorative) */}
      <div className="relative flex items-center justify-center gap-1.5 pb-3" aria-hidden>
        <span className="h-1.5 w-4 rounded-full bg-[#f6c500]" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
      </div>
    </a>
  );
}

export default HealthbossAd;
